/**
 * Reusable Privacy Policy content component.
 * Displays DeepThink's Privacy Policy content for use in pages or modals.
 * Update whenever the official Privacy Policy text or effective date changes.
 */
import { Box, Typography } from '@mui/material';

function PrivacyPolicyContent() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Privacy Policy – DeepThink Pte. Ltd.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Effective Date: 20 July 2026
      </Typography>

      <Typography variant="h6" component="h2">
        1. Introduction
      </Typography>
      <Typography variant="body1">
        DeepThink Pte. Ltd. ("DeepThink", "we", "us", "our") is committed to
        protecting your personal data in accordance with the Personal Data
        Protection Act 2012 of Singapore (PDPA).
      </Typography>
      <Typography variant="body1">
        This Privacy Policy applies to all services, applications, platforms,
        and websites operated by DeepThink Pte. Ltd., regardless of the brand or
        trade name under which they are offered (collectively, the "Platform").
      </Typography>
      <Typography variant="body1">
        By creating an account, activating your account, or otherwise using the
        Platform, you agree to the terms of this Privacy Policy.
      </Typography>

      <Typography variant="h6" component="h2">
        2. Types of Data We Collect
      </Typography>
      <Typography variant="subtitle1" component="h3">
        a. Students
      </Typography>
      <Typography variant="body1">• Name, age, school details</Typography>
      <Typography variant="body1">
        • Learning behaviour, engagement data, and assessments
      </Typography>
      <Typography variant="body1">
        • Login credentials, usage logs, preferences
      </Typography>
      <Typography variant="subtitle1" component="h3">
        b. Parents/Guardians (if applicable)
      </Typography>
      <Typography variant="body1">• Name, email, contact details</Typography>
      <Typography variant="body1">• Relationship to student</Typography>
      <Typography variant="body1">• Login credentials, usage logs</Typography>
      <Typography variant="subtitle1" component="h3">
        c. Teachers / Mentors / Administrators
      </Typography>
      <Typography variant="body1">
        • Name, email, organisation or school affiliation
      </Typography>
      <Typography variant="body1">• Role or designation</Typography>
      <Typography variant="body1">• Login credentials, usage logs</Typography>
      <Typography variant="subtitle1" component="h3">
        d. Automatically Collected Data
      </Typography>
      <Typography variant="body1">
        • Device information (type, OS, browser, identifiers)
      </Typography>
      <Typography variant="body1">
        • IP address, geolocation data (approximate)
      </Typography>
      <Typography variant="body1">
        • Platform usage behaviour, activity logs, analytics
      </Typography>
      <Typography variant="body1">
        • Cookies, pixels, and similar tracking technologies
      </Typography>
      <Typography variant="body1">
        • Aggregate analytics: We use a self-hosted Plausible Community Edition
        instance to measure page traffic and conversion patterns in aggregate.
        This service does not use cookies, fingerprinting, or any identifiers
        that can be tied back to you. Data stays on infrastructure we control.
      </Typography>

      <Typography variant="h6" component="h2">
        3. Legal Basis and Consent
      </Typography>
      <Typography variant="body1">
        We rely on the following legal bases:
      </Typography>
      <Typography variant="body1">
        • Consent: By activating your account and using our Platform, you
        consent to our collection, use, and disclosure of your personal data for
        the purposes described in this Policy.
      </Typography>
      <Typography variant="body1">
        • Deemed Consent: Where reasonably appropriate, we may rely on deemed
        consent under the PDPA, including deemed consent by conduct.
      </Typography>
      <Typography variant="body1">
        • Legitimate Interests: For analytics, marketing, research, fraud
        prevention, and platform improvement.
      </Typography>
      <Typography variant="body1">
        • Legal Obligation: Where required to comply with applicable laws,
        regulations, or investigations.
      </Typography>
      <Typography variant="body1">
        For users under 13 years old, verifiable parental or guardian consent is
        required before account activation. We treat children's personal data as
        sensitive and implement enhanced protection measures in accordance with
        Singapore's PDPA and the PDPC Advisory Guidelines on Children's Personal
        Data in the Digital Environment.
      </Typography>

      <Typography variant="h6" component="h2">
        4. Purpose of Collection and Use of Data
      </Typography>
      <Typography variant="body1">
        We may collect, use, and disclose personal data to:
      </Typography>
      <Typography variant="body1">
        • Operate, manage, and personalise your account and experience
      </Typography>
      <Typography variant="body1">
        • Provide, deliver, and improve our educational services and content
      </Typography>
      <Typography variant="body1">
        • Conduct analytics, profiling, and research on user behaviour and
        engagement
      </Typography>
      <Typography variant="body1">
        • Develop and deliver personalised content, offers, and services
      </Typography>
      <Typography variant="body1">
        • Use automated systems, machine learning, and artificial intelligence
        to personalise learning, assess mastery, recommend and generate content,
        provide feedback, and improve the Platform
      </Typography>
      <Typography variant="body1">
        • Provide marketing, promotions, surveys, and communications (including
        personalised or targeted advertising across our Platform and third-party
        platforms such as Google, Facebook, TikTok, and others)
      </Typography>
      <Typography variant="body1">
        • Enable interest-based advertising using cookies, device IDs, and other
        identifiers
      </Typography>
      <Typography variant="body1">
        • Share insights with advertisers, partners, and affiliates to support
        business operations and joint marketing activities
      </Typography>
      <Typography variant="body1">
        • Detect, investigate, and prevent fraud or misuse of the Platform
      </Typography>
      <Typography variant="body1">
        • Comply with laws, regulations, and enforcement requests
      </Typography>

      <Typography variant="h6" component="h2">
        5. Artificial Intelligence, Automated Processing, and Personalisation
      </Typography>
      <Typography variant="body1">
        To deliver and improve our educational services, we use automated
        systems, including statistical models, machine learning, and artificial
        intelligence ("AI"), to:
      </Typography>
      <Typography variant="body1">
        • Assess and estimate each student's mastery, strengths, and gaps
      </Typography>
      <Typography variant="body1">
        • Recommend topics, sequence practice, and adapt question difficulty
      </Typography>
      <Typography variant="body1">
        • Generate, select, and personalise practice content and feedback
      </Typography>
      <Typography variant="body1">
        • Analyse engagement to improve teaching, content, and the Platform
      </Typography>
      <Typography variant="body1">
        Some of these processes make automated decisions about what content and
        learning pathways to present. These decisions are advisory and operate
        alongside, and under the oversight of, our teachers and mentors; they do
        not produce legal or similarly significant effects on you without human
        involvement. You may contact our Data Protection Officer to ask about,
        or request human review of, a decision that materially affects you.
      </Typography>
      <Typography variant="body1">
        We may engage third-party technology and AI service providers to process
        personal data on our behalf for these purposes, under contractual
        safeguards and, where data is transferred outside Singapore, in
        accordance with Section 26 of the PDPA. We do not sell your personal
        data, and we do not permit these providers to use it for their own
        purposes.
      </Typography>
      <Typography variant="body1">
        If we ever use personal data you provided for other purposes to train or
        fine-tune a generative-AI model, and no exception under the PDPA
        applies, we will notify you of the purpose and the data involved, and
        give you a way to decline, before doing so. We do not use children's
        personal data for this without verifiable parental or guardian consent.
      </Typography>

      <Typography variant="h6" component="h2">
        6. Disclosure of Personal Data
      </Typography>
      <Typography variant="body1">We may share personal data with:</Typography>
      <Typography variant="body1">
        • Service providers, vendors, and IT/cloud hosting providers
      </Typography>
      <Typography variant="body1">
        • Technology and artificial-intelligence service providers that process
        personal data on our behalf to operate, secure, personalise, and improve
        the Platform
      </Typography>
      <Typography variant="body1">
        • Advertising networks, marketing partners, analytics providers, and
        social media platforms for targeted marketing and advertising delivery
      </Typography>
      <Typography variant="body1">
        • Affiliates, business partners, and group companies for service and
        marketing purposes
      </Typography>
      <Typography variant="body1">
        • Third-party authentication providers (Google, Apple, Microsoft), if
        used for login
      </Typography>
      <Typography variant="body1">
        • School administrators, mentors, or teachers managing student accounts
      </Typography>
      <Typography variant="body1">
        • Regulators, enforcement agencies, or legal advisers as required by law
      </Typography>
      <Typography variant="body1">
        We may also disclose or transfer personal data in connection with any
        merger, acquisition, restructuring, financing, or sale of assets
        involving DeepThink Pte. Ltd.
      </Typography>

      <Typography variant="h6" component="h2">
        7. International Transfers
      </Typography>
      <Typography variant="body1">
        Where personal data is transferred outside Singapore, we will comply
        with Section 26 of the PDPA and ensure that the receiving party is bound
        by comparable safeguards.
      </Typography>

      <Typography variant="h6" component="h2">
        8. Data Security
      </Typography>
      <Typography variant="body1">
        We implement reasonable security measures, including:
      </Typography>
      <Typography variant="body1">
        • SSL encryption and secure storage
      </Typography>
      <Typography variant="body1">
        • Access controls and administrative safeguards
      </Typography>
      <Typography variant="body1">
        • Periodic monitoring and security audits
      </Typography>
      <Typography variant="body1">
        However, no transmission method or storage system is completely secure.
        To the maximum extent permitted by law, we disclaim liability for any
        unauthorised access, use, or disclosure of personal data beyond our
        reasonable control.
      </Typography>

      <Typography variant="h6" component="h2">
        9. Cookies and Tracking Technologies
      </Typography>
      <Typography variant="body1">
        We use cookies, pixels, and tracking technologies to:
      </Typography>
      <Typography variant="body1">
        • Understand user interaction and behaviour
      </Typography>
      <Typography variant="body1">
        • Improve services and personalise content
      </Typography>
      <Typography variant="body1">
        • Enable interest-based advertising and targeted marketing
      </Typography>
      <Typography variant="body1">
        By using the Platform, you consent to our use of cookies and tracking
        technologies. Some features may not function properly if cookies are
        disabled.
      </Typography>

      <Typography variant="h6" component="h2">
        10. User Rights
      </Typography>
      <Typography variant="body1">
        Under the PDPA, you have the right to:
      </Typography>
      <Typography variant="body1">
        • Access and correct your personal data
      </Typography>
      <Typography variant="body1">
        • Withdraw consent (affecting future use only)
      </Typography>
      <Typography variant="body1">
        • Request deletion of your account and associated data
      </Typography>
      <Typography variant="body1">
        Requests may be directed to our Data Protection Officer (DPO):
        dpo@deepthink.sg. We will respond within 5 working days.
      </Typography>

      <Typography variant="h6" component="h2">
        11. Minors and Children's Data
      </Typography>
      <Typography variant="body1">
        Our Platform is designed to be used by students, including children,
        with appropriate parental or guardian consent.
      </Typography>
      <Typography variant="body1">
        • Users under the age of 13 (including children as young as 6) require
        verifiable parental or guardian consent before we collect, use, or
        disclose their personal data.
      </Typography>
      <Typography variant="body1">
        • We treat children's personal data as sensitive and implement enhanced
        security and protection measures.
      </Typography>
      <Typography variant="body1">
        • We follow data minimisation principles for children, collecting only
        the personal data necessary to provide our educational services.
      </Typography>
      <Typography variant="body1">
        • If we learn that personal data has been collected from a child under
        13 without verifiable parental or guardian consent, we will delete such
        data promptly.
      </Typography>
      <Typography variant="body1">
        • Parents and guardians may review, correct, or request deletion of
        their child's personal data by contacting our Data Protection Officer at
        dpo@deepthink.sg.
      </Typography>

      <Typography variant="h6" component="h2">
        12. Changes to This Policy
      </Typography>
      <Typography variant="body1">
        We may revise this Privacy Policy from time to time. Updates will be
        posted with the effective date. Continued use of the Platform after any
        revision constitutes acceptance of the updated terms.
      </Typography>
    </Box>
  );
}

export default PrivacyPolicyContent;
